/**
 * StaffLayout - Layout wrapper for Admin and Worker pages
 * iOS-inspired design with gray background and elevated cards
 */

import { cn } from '@/lib/utils';

export interface StaffLayoutProps {
  /** Page content */
  children: React.ReactNode;
  /** Page title */
  title?: string;
  /** Optional subtitle */
  subtitle?: string;
  /** Additional class names */
  className?: string;
}

export function StaffLayout({
  children,
  title,
  subtitle,
  className,
}: StaffLayoutProps) {
  return (
    <div className={cn(
      'h-screen flex flex-col bg-slate-100 overflow-hidden',
      className
    )}>
      {/* Header */}
      {title && (
        <header className="flex-shrink-0 bg-slate-100/80 backdrop-blur-sm border-b border-slate-200/50 px-4 py-3">
          <h1 className="text-xl font-bold text-slate-800">{title}</h1>
          {subtitle && (
            <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>
          )}
        </header>
      )}

      {/* Content - flex-1 to fill remaining space */}
      <main className="flex-1 min-h-0">
        {children}
      </main>
    </div>
  );
}

/**
 * StaffCard - Elevated card component for staff pages
 * White background with iOS-style shadow
 */
export interface StaffCardProps {
  children: React.ReactNode;
  className?: string;
  /** Remove padding (for custom content) */
  noPadding?: boolean;
}

export function StaffCard({ children, className, noPadding = false }: StaffCardProps) {
  return (
    <div className={cn(
      'bg-white rounded-2xl',
      'shadow-[0_2px_8px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.04)]',
      !noPadding && 'p-4',
      className
    )}>
      {children}
    </div>
  );
}

/**
 * StaffSection - Section with title and optional action
 */
export interface StaffSectionProps {
  children: React.ReactNode;
  title?: string;
  action?: React.ReactNode;
  className?: string;
}

export function StaffSection({ children, title, action, className }: StaffSectionProps) {
  return (
    <section className={cn('space-y-3', className)}>
      {(title || action) && (
        <div className="flex items-center justify-between px-1">
          {title && (
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
              {title}
            </h3>
          )}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}
